import i18n from '@/i18n'
import store from '@/store'
import { propertyContact } from '@/use/usePropertyContact'
import formatDateTime from './formatDateTime'
import { curatedErrorCopy } from './serverErrorAlert'

type BookingErrorCodes = {
  BookingNotFound: 'BookingNotFound'
  BookingNotActive: 'BookingNotActive'
  BookingNotValidForCheckin: 'BookingNotValidForCheckin'
  BookingNotAllowedToCheckinYet: 'BookingNotAllowedToCheckinYet'
  BookingStatusNotValidForCheckin: 'BookingStatusNotValidForCheckin'
  LastNameRequired: 'LastNameRequired'
}
type ErrorMetadata = {
  stayStartsAt?: string
}

type ErrorMessagesByCode<Type> = {
  [Property in keyof Type]: {
    title: string
    messages: string[]
  }
}

// `setAlert` has a single slot, so a code with no entry above used to replace whatever the
// interceptor had just shown with an alert that had no title and no message -- the blank screen
// in ENG-3129. Use the generic error copy for the code instead, and never render it empty.
function fallbackMessages(errorCode?: string) {
  const { t } = i18n.global
  const curated = curatedErrorCopy(errorCode)

  return {
    title: curated.title ?? t('alerts.errors.somethingWentWrong'),
    messages: [curated.message ?? t('alerts.errors.unspecified')],
  }
}

export default async function errorDispatcher(
  errorCode: keyof BookingErrorCodes | string | undefined,
  actionFunction: () => any,
  metadata?: ErrorMetadata
) {
  const { t } = i18n.global // removed "|| i18n" since { t } cannot be destructed from it

  const ERROR_CODE_MESSAGES: ErrorMessagesByCode<BookingErrorCodes> = {
    BookingNotFound: {
      title: t('alerts.errors.titles.BookingNotFound'),
      messages: [
        t('alerts.errors.messages.BookingNotFound[0]'),
        t('alerts.errors.messages.BookingNotFound[1]', {
          phoneNumber: propertyContact.value,
        }),
      ],
    },
    BookingNotActive: {
      title: t('alerts.errors.titles.BookingNotActive'),
      messages: [
        t('alerts.errors.messages.BookingNotActive[0]'),
        t('alerts.errors.messages.BookingNotActive[1]', {
          phoneNumber: propertyContact.value,
        }),
      ],
    },
    BookingNotValidForCheckin: {
      title: t('alerts.errors.titles.BookingNotValidForCheckin'),
      messages: [
        t('alerts.errors.messages.BookingNotValidForCheckin[0]'),
        t('alerts.errors.messages.BookingNotValidForCheckin[1]', {
          phoneNumber: propertyContact.value,
        }),
      ],
    },
    BookingStatusNotValidForCheckin: {
      title: t('alerts.errors.titles.BookingStatusNotValidForCheckin'),
      messages: [
        t('alerts.errors.messages.BookingStatusNotValidForCheckin[0]'),
        t('alerts.errors.messages.BookingStatusNotValidForCheckin[1]', {
          phoneNumber: propertyContact.value,
        }),
      ],
    },
    LastNameRequired: {
      title: t('alerts.errors.titles.LastNameRequired'),
      messages: [
        t('alerts.errors.messages.LastNameRequired[0]'),
        t('alerts.errors.messages.LastNameRequired[1]', {
          phoneNumber: propertyContact.value,
        }),
      ],
    },
    BookingNotAllowedToCheckinYet: {
      title: t('alerts.errors.titles.BookingNotAllowedToCheckinYet'),
      messages: [
        metadata?.stayStartsAt
          ? t('alerts.errors.messages.BookingNotAllowedToCheckinYet[0]', {
            formattedDate: formatDateTime(
              metadata?.stayStartsAt,
              i18n.global.locale?.value || 'en'
            ),
          })
          : t('alerts.errors.messages.BookingNotAllowedToCheckinYet[2]'),
        t('alerts.errors.messages.BookingNotAllowedToCheckinYet[1]', {
          phoneNumber: propertyContact.value,
        }),
      ],
    },
  }

  await store.dispatch(
    'setAlert',
    {
      ...(ERROR_CODE_MESSAGES[errorCode as keyof BookingErrorCodes] ||
        fallbackMessages(errorCode)),
      buttonAction: actionFunction,
      buttonText: t('general.close'),
    },
    { root: true }
  )
}
